import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/Layout';
import StickyActionHeader from '../../components/StickyActionHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../../components/ui/dialog';
import { 
  Plus, 
  Phone,
  EnvelopeSimple,
  WhatsappLogo,
  User,
  PencilSimple,
  Trash,
} from '@phosphor-icons/react';
import api from '../../services/api';
import { toast } from 'sonner';
import InfoTip from '../../components/InfoTip';

export default function KeyContacts() {
  const { currentEvent, hasAdminAccess } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [savingContact, setSavingContact] = useState(false);
  const [deletingContactId, setDeletingContactId] = useState('');
  const [newContact, setNewContact] = useState({
    name: '',
    role: '',
    phone: '',
    email: '',
  });

  useEffect(() => {
    fetchContacts();
  }, [currentEvent?.id]);

  const fetchContacts = async () => {
    if (!currentEvent?.id) return;
    try {
      const response = await api.get(`/events/${currentEvent.id}/contacts`);
      setContacts(response.data);
    } catch (error) {
      toast.error('Failed to fetch contacts');
    } finally {
      setLoading(false);
    }
  };

  const handleAddContact = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/events/${currentEvent.id}/contacts`, {
        ...newContact,
        email: newContact.email.trim() || null,
      });
      toast.success('Contact added successfully');
      setShowAddDialog(false);
      setNewContact({ name: '', role: '', phone: '', email: '' });
      fetchContacts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add contact');
    }
  };

  const openEditContact = (contact) => {
    setEditingContact({
      ...contact,
      email: contact.email || '',
      whatsapp: contact.whatsapp || '',
    });
  };

  const handleUpdateContact = async (e) => {
    e.preventDefault();
    if (!editingContact || !currentEvent?.id) return;
    setSavingContact(true);
    try {
      const response = await api.put(
        `/events/${currentEvent.id}/contacts/${editingContact.id}`,
        {
          name: editingContact.name,
          role: editingContact.role,
          phone: editingContact.phone,
          email: editingContact.email.trim() || null,
          whatsapp: editingContact.whatsapp.trim() || null,
        },
      );
      setContacts((current) => current.map(
        (contact) => (contact.id === response.data.id ? response.data : contact),
      ));
      setEditingContact(null);
      toast.success('Contact updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update contact');
    } finally {
      setSavingContact(false);
    }
  };

  const handleDeleteContact = async (contact) => {
    if (!currentEvent?.id) return;
    if (!window.confirm(`Delete ${contact.name} from key contacts?`)) return;
    setDeletingContactId(contact.id);
    try {
      await api.delete(`/events/${currentEvent.id}/contacts/${contact.id}`);
      setContacts((current) => current.filter((item) => item.id !== contact.id));
      toast.success('Contact deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete contact');
    } finally {
      setDeletingContactId('');
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <StickyActionHeader>
          <div className="flex items-center gap-1">
            <h1 className="font-serif text-2xl font-bold text-wedding-charcoal" data-testid="contacts-title">Key Contacts</h1>
            <InfoTip content="Add the important contacts guests may need during the event." label="About key contacts" />
          </div>
          {hasAdminAccess && (
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <div className="flex w-full sm:w-auto sm:justify-end" data-header-action-group>
                <DialogTrigger asChild>
                  <Button
                    className="bg-wedding-sage hover:bg-wedding-sage/90"
                    aria-label="Add key contact"
                    title="Add key contact"
                    data-header-action
                    data-testid="add-contact-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" /> Add Contact
                  </Button>
                </DialogTrigger>
              </div>
              <DialogContent className="max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Add Key Contact</DialogTitle>
                  <DialogDescription>Add an important contact for guests to reach out</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleAddContact} className="space-y-3">
                  <div>
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      value={newContact.name}
                      onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                      required
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="role">Role *</Label>
                    <Input
                      id="role"
                      value={newContact.role}
                      onChange={(e) => setNewContact({ ...newContact, role: e.target.value })}
                      placeholder="e.g., Event Manager, Logistics"
                      required
                      data-testid="contact-role-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone *</Label>
                    <Input
                      id="phone"
                      value={newContact.phone}
                      onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                      required
                      data-testid="contact-phone-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={newContact.email}
                      onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                      data-testid="contact-email-input"
                    />
                  </div>
                  <DialogFooter>
                    <Button type="submit" className="bg-wedding-sage hover:bg-wedding-sage/90" data-testid="save-contact-btn">
                      Add Contact
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </StickyActionHeader>

        <Dialog
          open={Boolean(editingContact)}
          onOpenChange={(open) => {
            if (!open && !savingContact) setEditingContact(null);
          }}
        >
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Key Contact</DialogTitle>
              <DialogDescription>Update the contact details shown to guests.</DialogDescription>
            </DialogHeader>
            {editingContact && (
              <form onSubmit={handleUpdateContact} className="space-y-3">
                <div>
                  <Label htmlFor="edit-contact-name">Name *</Label>
                  <Input
                    id="edit-contact-name"
                    value={editingContact.name}
                    onChange={(e) => setEditingContact({ ...editingContact, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="edit-contact-role">Role *</Label>
                  <Input
                    id="edit-contact-role"
                    value={editingContact.role}
                    onChange={(e) => setEditingContact({ ...editingContact, role: e.target.value })}
                    placeholder="e.g., Event Manager, Logistics"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="edit-contact-phone">Phone *</Label>
                  <Input
                    id="edit-contact-phone"
                    value={editingContact.phone}
                    onChange={(e) => setEditingContact({ ...editingContact, phone: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="edit-contact-email">Email</Label>
                  <Input
                    id="edit-contact-email"
                    type="email"
                    value={editingContact.email}
                    onChange={(e) => setEditingContact({ ...editingContact, email: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="edit-contact-whatsapp">WhatsApp Number</Label>
                  <Input
                    id="edit-contact-whatsapp"
                    value={editingContact.whatsapp}
                    onChange={(e) => setEditingContact({ ...editingContact, whatsapp: e.target.value })}
                    placeholder="Include country code"
                  />
                </div>
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setEditingContact(null)}
                    disabled={savingContact}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="bg-wedding-sage hover:bg-wedding-sage/90"
                    disabled={savingContact}
                  >
                    {savingContact ? 'Saving...' : 'Save Changes'}
                  </Button>
                </DialogFooter>
              </form>
            )}
          </DialogContent>
        </Dialog>

        {/* Contact Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {contacts.map((contact) => (
            <Card key={contact.id} className="hover:shadow-md transition-shadow" data-testid={`contact-${contact.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-full bg-wedding-sage/10 flex items-center justify-center flex-shrink-0">
                    <User className="w-6 h-6 text-wedding-sage" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold truncate">{contact.name}</h3>
                    <p className="text-sm text-wedding-gold font-medium">{contact.role}</p>
                  </div>
                  {hasAdminAccess && (
                    <div className="flex shrink-0 items-center gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={`Edit ${contact.name}`}
                        title="Edit contact"
                        onClick={() => openEditContact(contact)}
                      >
                        <PencilSimple className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                        aria-label={`Delete ${contact.name}`}
                        title="Delete contact"
                        disabled={deletingContactId === contact.id}
                        onClick={() => handleDeleteContact(contact)}
                      >
                        <Trash className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
                <div className="mt-4 space-y-2">
                  <a 
                    href={`tel:${contact.phone}`}
                    className="flex items-center gap-2 text-sm text-wedding-sage hover:underline"
                  >
                    <Phone className="w-4 h-4" /> {contact.phone}
                  </a>
                  {contact.email && (
                    <a 
                      href={`mailto:${contact.email}`}
                      className="flex items-center gap-2 text-sm text-muted-foreground hover:text-wedding-sage"
                    >
                      <EnvelopeSimple className="w-4 h-4" /> {contact.email}
                    </a>
                  )}
                  {contact.whatsapp && (
                    <a 
                      href={`https://wa.me/${contact.whatsapp.replace(/[^0-9]/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-sm text-green-600 hover:underline"
                    >
                      <WhatsappLogo className="w-4 h-4" weight="fill" /> WhatsApp
                    </a>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
          {contacts.length === 0 && !loading && (
            <Card className="col-span-full">
              <CardContent className="py-12 text-center">
                <Phone className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No contacts added yet. Add key contacts for guests to reach out.</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
